const pickBy = require('lodash/pickBy');
const moment = require('moment');
const axios = require('axios');
const AxiosFactory = require('./axios/AxiosFactory');
const { getPortalFilters } = require('./endpoints/PortalFilters');
const { ECHO_WIDGET, BOOTSTRAP_CONFIG_KEYS } = require('Constants/WidgetConfig');

const echoClient = AxiosFactory.createEchoInstance();
const kubeClient = AxiosFactory.createKubeInstance();

const kubeInstance = kubeClient.getInstance();
const switchInstance = AxiosFactory.createSwitchInstance().getInstance();
let echoInstance = echoClient.getInstance();

const getCustomLabels = ({ channelId, propertyCode, languageCode }) => {
    const sanitizedChannelId = channelId || 'ibe';
    const params = { languageCode };
    const targetEndpoint = !!propertyCode
        ? `api/channels/${sanitizedChannelId}/customLabel/${propertyCode}`
        : `api/channels/${sanitizedChannelId}/customLabel`;

    return switchInstance.get(targetEndpoint, { params });
};

const replaceEchoUrl = (url) => {
    if (url) {
        echoClient.setUrl(url);
        echoInstance = echoClient.getInstance();
    }
};

const getChannelData = params => kubeInstance
    .post('/channels', params)
    .then(response => response.data || null)
    .catch(() => null);

const getStyles = (params) => kubeInstance
    .post('/styling', params)
    .then(response => response.data || null)
    .catch(() => null);

function mapChannelSettingsToSortOrder(channelSettings) {
    if (channelSettings?.offersSorting === 'Native') {
        return 'native';
    }

    const descPriceOpts = [ 'PriceDesc', 'price-desc' ];

    if (descPriceOpts.includes(channelSettings?.offersSorting)) {
        return 'price-desc';
    }

    return 'price';
}

const getData = (params, keysToExclude = []) => kubeInstance
    .post('/channels', { ...params, user: { ...params.user }, source: 'widget' })
    .then((response) => {
        const { channelSettings, styleCustomizations } = response?.data;
        const settingsFields = Object.values(BOOTSTRAP_CONFIG_KEYS);

        // Returning just those params of interest for widgets.
        const widgetsSettings = pickBy(channelSettings, (value, key) => settingsFields.includes(key));

        // This is a special case, the key is different in the backend (promotionCodeEnabled vs promotionalCodeEnabled)
        if (widgetsSettings.promotionCodeEnabled) {
            widgetsSettings.promotionalCodeEnabled = widgetsSettings.promotionCodeEnabled;
            delete widgetsSettings.promotionCodeEnabled;
        }

        widgetsSettings.defaultOccupancy = widgetsSettings.defaultOccupancyConf;
        delete widgetsSettings.defaultOccupancyConf;

        // These params need additional processing, they come with another key or unexpected structure
        const additionalKeysStrategy = {
            currency: () => {
                const userCurrency = channelSettings?.currencies?.user || null;
                const activeCurrency = channelSettings?.currencies?.active || null;

                const userCurrencyAvailable = channelSettings?.currencies?.available
                    ?.find(currency => JSON.stringify(currency) === JSON.stringify(userCurrency));

                return userCurrencyAvailable || activeCurrency;
            },
            maxCheckoutDate: () => channelSettings && (channelSettings[BOOTSTRAP_CONFIG_KEYS.MAX_CHECKOUT_OFFSET]
                ? moment().add(channelSettings[BOOTSTRAP_CONFIG_KEYS.MAX_CHECKOUT_OFFSET], 'd')
                    .format('YYYY-MM-DD') : null),
            availableCurrencies: () => {
                if (channelSettings?.currencies?.available?.length) {
                    const result = {};
                    channelSettings.currencies.available.forEach((currency) => {
                        result[currency.code] = currency;
                    });

                    return result;
                }
                return null;
            },
            sortOrder               : () => mapChannelSettingsToSortOrder(channelSettings),
            ntpDomain               : () => channelSettings?.ntpDomain || null,
            los                     : () => channelSettings.defaultLengthOfStay,
            channelId               : () => channelSettings.id,
            offersSortingDefault    : () => channelSettings.offersSortingDefault,
            recommendedSortCriteria : () => channelSettings.recommendedSortCriteria,
            country                 : () => channelSettings.country,
        };

        Object.keys(additionalKeysStrategy).filter(key => !keysToExclude.includes(key))
            .forEach((key) => {
                widgetsSettings[key] = additionalKeysStrategy[key]();
            });

        return {
            settings: widgetsSettings,
            customizations: {
                styles: styleCustomizations?.styles || '',
                stylesUrl: styleCustomizations?.stylesUrl || null,
            },
            meta: {
                ipAddress: channelSettings?.ipAddress,
            },
        };
    })
    .catch((err) => {
        /* eslint-disable */
        console.log('pickBy ', pickBy);
        console.error('Widget configuration has failed, please contact an admin');
        return { settings: {}, customizations: { styles: '', stylesUrl: null } };
        /* eslint-enabled */

    });

const getTrackingModules = () => kubeInstance
    .get(`/tracking`)
    .then(response => response.data)
    .catch((e) => {
        throw e;
    });

const getWidgetsRooms = params => kubeInstance
    .post(`/widgets/rooms`, params)
    .then(response => response.data)
    .catch((error) => {

        throw error;

    });

const getRates = (params, widgetType) => {
    const clientToUse = widgetType === ECHO_WIDGET ? echoInstance : kubeInstance;
    const urlToUse = widgetType === ECHO_WIDGET ? `/widgets/echo/offers` : `/widgets/rates`;
    const settingsToUse = widgetType === ECHO_WIDGET ? { withCredentials: true } : {};

    return clientToUse.post(`${urlToUse}`, params, settingsToUse)
        .then(async (response) => {

            const { shouldUpgradeTier, shouldHideHotel } = response.data;

            const shouldCallGetRooms = shouldUpgradeTier || shouldHideHotel;

            return shouldCallGetRooms ? getWidgetsRooms(params).then(widgetsRooms => ({
                ...widgetsRooms,
                warnings: {
                    shouldUpgradeTier,
                    shouldHideHotel,
                },
            }))
                .catch((error) => {

                    throw error;

                }) : { ...response.data, warnings: {} };

        })
        .catch((e) => {

            if (e.response.status === 403) {

                return getWidgetsRooms(params).then(widgetsRooms => ({
                    ...widgetsRooms,
                    warnings: { staticInfoRequested: true },
                }))
                    .catch((error) => {

                        throw error;

                    });

            }

            throw e;

        });

};

const getTranslations = locale => axios
    .get(`${CDN_URL}/langs/${locale}.json`)
    .then(response => response.data)
    .catch((e) => {
        throw e;
    });

const getPropertyInfo = params => kubeInstance
    .post(`properties`, params)
    .then(response => response.data)
    .catch((e) => {
        throw e;
    });

const getConfigFromCdn = cdnUrl => axios
    .get(`${cdnUrl}`)
    .then(response => response.data)
    .catch((e) => {
        throw e;
    });

const getHotelsList = (channelId, params) => {

    return kubeInstance
    .post(`/channels/${channelId}/offers`, {
        ...params,
        showDemoProperties: true,
    })
    .then(response => response.data)
    .catch((e) => {
        throw e;
    });
}

const getPricelessHotelsListNtp = params => echoInstance
    .post(`/widgets/hotels-priceless`, params)
    .then(response => response.data)
    .catch((e) => {
        throw e;
    });

const getEeHotelsList = (params) => {
    return echoInstance.post('/widgets/ee/hotels', params, { withCredentials: true })
        .then(response => response.data)
        .catch((e) => {
            if (e.response?.status === 403) {
                return getPricelessHotelsListNtp(params);
            }
            throw e;
        });
};

const getDestinations = params => kubeInstance
    .post(`/static/regions/v2`, params)
    .then(response => response.data)
    .catch((e) => {
        throw e;
    });

const getSuggestions = params => kubeInstance
    .post(`/autocomplete`, params)
    .then(response => response.data)
    .catch((e) => {
        throw e;
    });

const getRegionsHotels = params => kubeInstance
    .post(`/widgets/regions-hotels`, params)
    .then(response => response.data)
    .catch((e) => {
        throw e;
    });

const getUserInfo = () => echoInstance.get(`/users`, { withCredentials: true })
    .then(response => response.data)
    .catch((e) => {
        throw e;
    });

const getUserCountry = params => kubeInstance
    .post(`/widgets/getUserCountry`, params)
    .then((response) => {
        return response.data
    })
    .catch((e) => {
        throw e;
    });

const getTrustYouProfile = ({ trustYouId, language }) => axios
    .get(`https://api.trustyou.com/hotels/${trustYouId}/seal.json?lang=${language}`)
    .then(response => response.data)
    .catch((e) => {
        throw e;
    });

const getTrustYouReviews = ({ trustYouId, language }) => axios
    .get(`https://api.trustyou.com/hotels/${trustYouId}/meta_review.json?lang=${language}`)
    .then(response => response.data)
    .catch((e) => {
        throw e;
    });

const getAssociatedCountries = async (params) => {

    const channelRequestParams = {
        domain : params.domain,
        user   : params.user,
    };
    const channelData = await getChannelData(channelRequestParams);
    return channelData ? Promise.resolve(channelData.channelSettings.associatedCountries)
        : Promise.resolve(false);

};
const getIpURL = () => kubeInstance
    .get('/ip-user')
    .then(response => response.data)
    .catch((e) => {
        throw e;
    });

const getClassifications = ({ channelId, languageCode }) => kubeInstance
    .get('/classfications', { params: { channelId, languageCode: languageCode.toUpperCase() }})
    .then(response => response.data)
    .catch((e) => {
        throw e;
    });

const getNtpEnvironment = () => kubeInstance.get('/environment')
    .then(response => response.data)
    .catch((e) => {
        throw e;
    });

const getRewardInfo = (params) => {

    return kubeInstance.get('/widgets/reward-program', { params: { ...params,  cleanEmptyStrings: true } })
    .then(response => response.data)
    .catch((e) => {
        throw e;
    });
};

const validateCoupon = ({ couponCode, channelId }) => kubeInstance
    .get('/coupons', {params:{couponCode,channelId}} )
    .then(response => response.data)
    .catch((e) =>  { throw e });

const getGooglePlaces = (params) => kubeInstance
    .get('/google-places', { params })
    .then(response => response.data)
    .catch((e) => {  throw e });

const getEarliestBookingDates = (propertyCode, params) => {
    const url = `/api/properties/${propertyCode}/bookingDates`;
    return switchInstance.get(url, { params })
    .then(response => response.data)
    .catch((e) => {
        throw e;
    });
}

const getRatesAverage = (params, rateCodes) => {
    const url = `/switch/latest/json/ratesAverage.json?${rateCodes}`;
    return switchInstance.get(url, { params })
    .then(response => response.data)
    .catch((e) => {
        throw e;
    });
}

module.exports = {
    getData,
    getTranslations,
    getRates,
    getConfigFromCdn,
    getHotelsList,
    getEeHotelsList,
    getPropertyInfo,
    getDestinations,
    getRegionsHotels,
    getUserInfo,
    getUserCountry,
    getTrackingModules,
    getTrustYouProfile,
    getTrustYouReviews,
    getSuggestions,
    getChannelData,
    getAssociatedCountries,
    getIpURL,
    replaceEchoUrl,
    getClassifications,
    getNtpEnvironment,
    getRewardInfo,
    validateCoupon,
    getGooglePlaces,
    getPortalFilters,
    getCustomLabels,
    getStyles,
    getEarliestBookingDates,
    getRatesAverage,
};
